import { fn, INamed } from '../utils';
import Notifications = fn.Notifications;
import { IPageObj, IPageProperty, TPagePropRefs } from '../nav/navtypes';
import { NavPropertyItem } from './navpropertyitem';
import { NavPageItem } from './navpageitem';
import { NavPageItems } from './navpageitems';
import { NavClientService } from './navclientservice';
import { NavPropertyContainer } from './navpropertycontainer';
import { IsNavPageItems, RegisterIndirection } from './indirection';
import { Observable } from './navmodel';
import { IDMSUILine, INavMethodConfig } from './navdata';

/** Root for Functionality Common to
       * - a Single Page-Item synced via Read/Update
       * - a Set of PageItems synced via ReadMultiple/UpdateMultiple
       */
export class NavItemAnchor<TPage extends IPageObj = IPageObj> extends Observable {
    constructor(public readonly Service: NavClientService, public Source: NavPageItem<TPage> | NavPageItems<TPage>) {
        super();
        this.Mandatory = new NavPropertyContainer(this, "Mandatory");
        this.Readonly = new NavPropertyContainer(this, "Readonly");
        this.InVisible = new NavPropertyContainer(this, "InVisible");
    }

    /** InstanceId for Confirm Operation in out custom Confirm Framework */
    public InstanceID: string;
    /** Mandatory State Information  */
    public Mandatory: NavPropertyContainer;
    /**  Readonly State Information */
    public Readonly: NavPropertyContainer & { /** Needed for Special Readonly Logic */ ExplicitUnlocked?: IPageProperty[] };
    /** Hidden State Information */
    public InVisible: NavPropertyContainer;
    /** Marker : an Enqueued Operation (e.g. an Update) is Currently in Progress */
    public InOperation: boolean;

    /** Register Notification of UpdateState (= IsInOperation) state */
    public RegisterUpdateStateChanged(callback: (state: { Update: boolean; Messages: boolean }) => void) {
        return this.updateNotifications.bind(callback);
    }
    private updateNotifications = new Notifications<{ Update: boolean, Messages?: boolean }>();

    /** Enqueue Operation  */
    public EnqueueOperation<T>(resolvePromise: () => PromiseLike<T>) {
        var me = this,
            $c = this.Service.$c,
            enqueueFollowed = false; // Marker : another Enqueue after this?

        this.trigger("_NavEnqueueOperation");
        // get Notified about next enqueue Operation
        this.one("_NavEnqueueOperation", () => enqueueFollowed = true);

        // allow Nesting of Enqueue calls
        if (this.enqueCounter > 0)
            return $c.when(resolvePromise());

        var currConfirmCounter = 0;
        var set_IsInOperation = (val: boolean) => {
            if (val !== me.InOperation) {
                me.InOperation = val;
                me.updateNotifications.trigger({
                    Update: me.InOperation,
                    Messages: !me.InOperation && (me._confirmCounter != currConfirmCounter)
                });
                if (me.InOperation)
                    currConfirmCounter = me._confirmCounter;
            }
        };

        var currResolveAction = () => {
            this.enqueCounter++;
            try {
                var currentResolved = $c.when(resolvePromise());
            } catch (err) {
                // currentResolved = $c.reject(err);
                // --> HACK: ensure 'order' of deferred operations ...  sort of
                currentResolved = $c.timeout(0).then(() => { throw err; } ); // when() $c.reject(err);

                if (!currentResolved || !fn.isFunction(currentResolved.then)) {
                    throw new Error("Invalid Promise Resolve Result : " + currentResolved);
                }
            }
            finally {
                this.enqueCounter--;
            }

            $c.finally(currentResolved, () => {
                set_IsInOperation(--me.currOpCounter > 0);
                if (me.currOpCounter == 0 && !enqueueFollowed) {
                    me.currOp = null; // necessary? (to be sure, garbage collection ...??)
                }
            });

            set_IsInOperation(++me.currOpCounter > 0);

            return currentResolved;
        };

        var result = me.currOp = (me.currOp || $c.when()).then(currResolveAction, err => currResolveAction());

        set_IsInOperation(true);

        return result;
    }
    private currOpCounter = 0; // necessary?
    private enqueCounter = 0;
    private currOp: PromiseLike<any>;

    /** Promise that gets deferred after an possibly running Promise gets resolved **/
    public AfterOperations() {
        return this.currOp || this.Service.$c.when();
    }

    /** Register Confirm Notifications Interceptors */
    RegisterConfirm(callback: (confirmInfo: { Method: INavMethodConfig; UIItem: IDMSUILine }) => void) {
        return this.confirmNotifications.bind(callback);
    }
    private confirmNotifications = new Notifications<{ Method: INavMethodConfig; UIItem: IDMSUILine }>();

    /** INTERNAL : Fire Confirm Notifications */
    FireConfirm(method: INavMethodConfig, item: IDMSUILine) {
        this._confirmCounter++;
        this.confirmNotifications.trigger({ Method: method, UIItem: item });
    }
    private _confirmCounter = 0;

    /** Start Update after Currently Running updates finshed, onUpdate Action will be called right before this Update Operation gets started */
    public Update(OnUpdate?: () => void, noSync?: boolean) {
        var source = this.Source;

        return this.EnqueueOperation(() => {
            if (OnUpdate)
                OnUpdate();
            var targetObj = this.Source.Container || this.Source;
            return this.Service.executeUpdate({ Items: targetObj }).then((newItem) => {
                // Übertrage die Werte von der Upgedaten Instanz
                if (newItem) {
                    if (source.IsNew)
                        delete source.IsNew;
                }
                return source;
            }, (err) => {
                // Im Fehlerfall : Lade aktuelle Daten vom server!
                // (evtl. noch ausstehenden Änderungen gehen so verloren!?!)
                if (noSync !== true) {
                    return targetObj.RevertItem(true).then(() => {
                        throw err;
                    });
                }
                throw err;
            });
        });
    }

    /** Start Method Execution after Currently Running updates finshed, onUpdate Action will be called right before this Update Operation gets started
    * Return value : "Result" : Result of Method
    * TODO :
    * - Include Out Parameters of Method on Server Side)
    * - Do Not ReRead Item on Server Side if not configured (TODO : extend configuration)
     */
    public ExecuteMethod(method: INavMethodConfig, OnUpdate?: () => void, noEnqueue?: boolean): PromiseLike<{ [name: string]: any }> {
        var optEnqueue = noEnqueue ? ((a: () => PromiseLike<any>) => a()) : (a: () => PromiseLike<any>) => this.EnqueueOperation(a);
        return optEnqueue(() => {
            if (OnUpdate)
                OnUpdate();
            var target = this.Source.Container || this.Source;
            return this.Service.ExecuteMethod(target, method).then<{ [name: string]: any }>((newItem) => {
                return newItem.MethodResult;
            }, (err) => {
                // Im Fehlerfall : Lade aktuelle Daten vom server!
                // (evtl. noch ausstehenden Änderungen gehen so verloren!?!)

                target.RevertItem(true);
                throw err; // WOut throwing the returned Promise will succeed?
            });
        });
    }

    /** register Property Changed Notification (prop = null or '*' means : all changes get notified, returns deregistration function */
    RegisterPropertyChanged(propRef: TPagePropRefs<TPage>,
        callback: (ev: { Prop: IPageProperty; Item: NavPropertyItem }) => void): () => void {
        if (Array.isArray(propRef)) {
            // Handle Array Registration
            var props = propRef.map(p => this.Source.ResolveProperty(p));

            return this.RegisterPropertyChanged("*", ev => {
                // Only callback if  prop is registered
                if (props.indexOf(ev.Prop) < 0)
                    return;

                callback(ev);
            });
        } else {
            // Handle Single Item Registration
            if (!propRef || propRef == "*") // means : all Props
                var prop = "*";
            else
                prop = this.Source.ResolveProperty(propRef, true).Path;
            var r: Notifications<{ Prop: IPageProperty; Item: NavPropertyItem }> = this.notifications[prop] || (r = this.notifications[prop] = new Notifications<{ Prop: IPageProperty; Item: NavPropertyItem }>());
            return r.bind(callback);
        }
    }

    /** INTERNAL : Fire Notifications about Value Changed */
    FireValueChanged(item: NavPropertyItem, prop: IPageProperty) {
        var notifications = this.notifications,
            ev = { Prop: prop, Item: item },
            root = item.Root;

        [notifications[prop.Path], notifications["*"]].forEach(r => r && r.trigger(ev));
    }
    private notifications: INamed<Notifications<{ Prop: IPageProperty; Item: NavPropertyItem }>> = {};

    /** Find First Invalid Property Property Value Item */
    public FindFirstInvalid(currItem?: NavPageItem<TPage>, includeFilter?: (item: NavPropertyItem, prop: IPageProperty) => boolean) {
        var items = IsNavPageItems<TPage>(this.Source) ? fn.ToArray(this.Source.Items) : [this.Source];
        includeFilter = includeFilter || fn.ToFunc(true);
        // check current first if given
        if (currItem)
            fn.sort(items, item => (item == currItem) ? 0 : 1);
        var mandProps = this.Mandatory.GetItems().map(item => this.Source.ClientInfo.AllProperties[item]).filter(fn.isSet);
        for (var i = 0; i < mandProps.length; i++) {
            let currProp = mandProps[i];
            for (var j = 0; j < items.length; j++) {
                var currItem = items[j];
                if (currProp.ParentProp) {
                    var lineItems = currItem.GetLineData(currProp.ParentProp).Items;
                    for (var k = 0; k < lineItems.length; k++) {
                        if (lineItems[k].GetIsInvalid(currProp) && includeFilter(lineItems[k], currProp))
                            return {
                                Item: lineItems[k],
                                Prop: currProp
                            };
                    }
                } else {
                    if (currItem.GetIsInvalid(currProp) && includeFilter(currItem, currProp))
                        return {
                            Item: currItem,
                            Prop: currProp
                        };
                }
            }
        }
    }
}
RegisterIndirection("NavItemAnchor", NavItemAnchor);
